import { ChevronRight } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { NavLink } from 'react-router'

import { cn } from '../../utils/cn'
import { Button } from '../ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import type { SidebarIcon } from './model'
import { SIDEBAR_ROW } from './SidebarNavLink'

/** Between products: the drawer's full width, past the nav's padding, so each product reads as its own block. */
export const PRODUCT_RULE = 'border-ink-100 -mx-2 my-1 border-t'

/** A phone menu row: rounded grey fills that fade in and out, and one on the row you are on. */
const DRAWER_ROW =
  'rounded-xl transition-[background-color,color] duration-200 ease-out aria-[current=page]:bg-ink-50'

/** A product heads its pages: a taller row with larger text than the pages under it. */
const PRODUCT_ROW = 'h-10 cursor-pointer justify-start text-md font-semibold has-[>svg]:px-2'

export interface DrawerPage {
  title: string
  /** Absolute for another app's page, otherwise a route in this one. */
  url: string
  exact?: boolean
  /** Starts a new run of pages, with a rule above it. */
  ruleBefore?: boolean
}

export interface DrawerProduct<T extends string> {
  value: T
  label: string
  icon: SidebarIcon
  /** Its pages; with none, the product is a single row that runs `onSelect` or follows `href`. */
  pages: DrawerPage[]
  onSelect?: () => void
  /** Where the row goes when it has no pages; absolute for another app. */
  href?: string
  /** Set apart from the products after it by a rule, as Cai is. */
  lead?: boolean
}

interface ProductNavDrawerProps<T extends string> {
  products: DrawerProduct<T>[]
  /** The open product, which starts expanded. */
  active: T
  /** Closes the drawer once something is picked. */
  onNavigate: () => void
  /** Rows after the products, such as staff's console switch or another product's groups. */
  children?: ReactNode
}

/** Every Cai app's phone menu: each product as a group that opens onto its pages. */
export function ProductNavDrawer<T extends string>({
  products,
  active,
  onNavigate,
  children,
}: ProductNavDrawerProps<T>) {
  return (
    // Scrolls on its own: the sheet is viewport-high and an open group can outgrow a short phone.
    <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
      <nav aria-label="Cai products" className="text-ink-600 flex flex-col gap-1 p-2">
        <DrawerProductRows products={products} active={active} onNavigate={onNavigate} />
        {children}
      </nav>
    </div>
  )
}

/** The product rows alone, for a product that puts its groups into a drawer it does not own. */
export function DrawerProductRows<T extends string>({
  products,
  active,
  onNavigate,
}: Omit<ProductNavDrawerProps<T>, 'children'>) {
  const [expanded, setExpanded] = useState<T | null>(active)

  return products.map((product) => {
    const row = (
      <DrawerProductRow
        key={product.value}
        product={product}
        current={product.value === active}
        open={expanded === product.value}
        onOpenChange={(open) => setExpanded(open ? product.value : null)}
        onNavigate={onNavigate}
      />
    )
    if (!product.lead) return row
    return [row, <span key={`${product.value}-rule`} className={PRODUCT_RULE} />]
  })
}

function DrawerProductRow<T extends string>({
  product: { label, icon: Icon, pages, onSelect, href },
  current,
  open,
  onOpenChange,
  onNavigate,
}: {
  product: DrawerProduct<T>
  current: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate: () => void
}) {
  const content = (
    <>
      <Icon aria-hidden="true" />
      <span>{label}</span>
    </>
  )

  if (pages.length === 0 && href) {
    // Another app's product is a full page load, and never the page we are on.
    if (href.startsWith('http')) {
      return (
        <a href={href} className={cn(SIDEBAR_ROW, PRODUCT_ROW, DRAWER_ROW)}>
          {content}
        </a>
      )
    }
    return (
      <NavLink to={href} onClick={onNavigate} className={cn(SIDEBAR_ROW, PRODUCT_ROW, DRAWER_ROW)}>
        {content}
      </NavLink>
    )
  }

  if (pages.length === 0) {
    return (
      <Button
        variant="ghost"
        aria-current={current ? 'page' : undefined}
        onClick={() => {
          onSelect?.()
          onNavigate()
        }}
        className={cn(SIDEBAR_ROW, PRODUCT_ROW, DRAWER_ROW)}
      >
        {content}
      </Button>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger
        className={cn(SIDEBAR_ROW, PRODUCT_ROW, DRAWER_ROW, 'group/product data-[state=open]:text-ink-900')}
      >
        {content}
        <ChevronRight
          aria-hidden="true"
          className="text-ink-300! ml-auto transition-transform group-data-[state=open]/product:rotate-90 motion-reduce:transition-none"
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="flex flex-col gap-0.5 py-1 pl-6">
          {pages.map((page) => [
            page.ruleBefore && (
              <li key={`${page.url}-rule`} aria-hidden="true" className="border-ink-100 my-1 border-t" />
            ),
            <li key={page.url}>
              <DrawerPageRow page={page} onNavigate={onNavigate} />
            </li>,
          ])}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

function DrawerPageRow({ page, onNavigate }: { page: DrawerPage; onNavigate: () => void }) {
  // Another app's page is a full page load, and never the page we are on.
  if (page.url.startsWith('http')) {
    return (
      <a href={page.url} className={cn(SIDEBAR_ROW, DRAWER_ROW)}>
        <span>{page.title}</span>
      </a>
    )
  }
  return (
    <NavLink to={page.url} end={page.exact} onClick={onNavigate} className={cn(SIDEBAR_ROW, DRAWER_ROW)}>
      <span>{page.title}</span>
    </NavLink>
  )
}
